/**
 * Contact Form Wholesale Fields Toggle
 * Shows/hides wholesale fields and makes them required when "Wholesale Enquiries" is selected
 * Works with both native select and Choices.js select
 */

(function() {
  'use strict';

  // Wholesale subject values to check for
  const WHOLESALE_VALUES = ['Wholesale Enquiries', 'wholesale_inquiry'];
  
  /**
   * Get the selected value from select (handles both native and Choices.js)
   */
  function getSelectedValue(selectElement) {
    // For Choices.js, the value is still in the original select element
    // but we need to check the actual select value
    return selectElement.value || '';
  }
  
  /**
   * Get the selected text from select (handles both native and Choices.js)
   */
  function getSelectedText(selectElement) {
    // Native select
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    if (selectedOption) {
      return selectedOption.text || selectedOption.value;
    }
    return '';
  }
  
  /**
   * Initialize the wholesale fields functionality
   */
  function initWholesaleFields() {
    const subjectSelect = document.querySelector('[data-contact-subject-select]');
    const wholesaleFields = document.querySelector('[data-wholesale-fields]');
    
    if (!subjectSelect || !wholesaleFields) {
      return;
    }

    const wholesaleInputs = wholesaleFields.querySelectorAll('[data-wholesale-input]');
    const wholesaleRadioGroups = wholesaleFields.querySelectorAll('.field--radio-group');
    const wholesaleCheckboxGroups = wholesaleFields.querySelectorAll('.field--checkbox-group');
    const form = subjectSelect.closest('form');
    
    // Get the monthly retail volume section and retail radio buttons
    const monthlyRetailVolumeSection = wholesaleFields.querySelector('[data-monthly-retail-volume-section]');
    const retailRadioButtons = wholesaleFields.querySelectorAll('input[name="contact[Do You Retail]"]');
    const monthlyRetailVolumeRadioGroup = monthlyRetailVolumeSection ? 
      monthlyRetailVolumeSection.querySelector('.field--radio-group') : null;
    
    // Get the "Your message" textarea and its parent field
    const messageTextarea = form ? form.querySelector('textarea[id$="-body"]') : null;
    const messageField = messageTextarea ? messageTextarea.closest('.field') : null;
    
    // Get the submit button
    const submitButton = form ? form.querySelector('button[type="submit"]') : null;
    const wholesaleSubmitText = submitButton ? submitButton.getAttribute('data-wholesale-submit-text') : null;
    const defaultSubmitText = submitButton ? submitButton.getAttribute('data-default-submit-text') : null;
    
    /**
     * Validate radio button groups - at least one must be selected
     */
    function validateRadioGroup(radioGroup) {
      const radios = radioGroup.querySelectorAll('input[type="radio"]');
      const isChecked = Array.from(radios).some(radio => radio.checked);
      return isChecked;
    }
    
    /**
     * Validate checkbox group - at least one must be selected in the group
     */
    function validateCheckboxGroup(checkboxGroup) {
      const checkboxes = checkboxGroup.querySelectorAll('input[type="checkbox"]');
      if (checkboxes.length === 0) return true;
      return Array.from(checkboxes).some(checkbox => checkbox.checked);
    }
    
    /**
     * Get the selected value for "Do You Retail" radio group
     */
    function getRetailSelection() {
      const selectedRetail = Array.from(retailRadioButtons).find(radio => radio.checked);
      if (!selectedRetail) return null;
      
      // Get the translation value for "yes" to compare
      // We'll check both the value and if it's the "yes" radio button
      const yesRadio = Array.from(retailRadioButtons).find(radio => 
        radio.id && radio.id.includes('retail-yes')
      );
      
      if (yesRadio && selectedRetail === yesRadio) {
        return 'yes';
      }
      return 'no';
    }
    
    /**
     * Toggle monthly retail volume section visibility and requirement
     */
    function toggleMonthlyRetailVolume() {
      if (!monthlyRetailVolumeSection || !monthlyRetailVolumeRadioGroup) return;
      
      const retailSelection = getRetailSelection();
      const isRetailYes = retailSelection === 'yes';
      
      if (isRetailYes) {
        // Show the section
        monthlyRetailVolumeSection.classList.remove('hidden');
        // Mark as required for validation (we'll handle this in validation)
      } else {
        // Hide the section
        monthlyRetailVolumeSection.classList.add('hidden');
        // Clear any selected value
        const radios = monthlyRetailVolumeRadioGroup.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          radio.checked = false;
        });
        // Remove error class
        monthlyRetailVolumeRadioGroup.classList.remove('field--error');
      }
    }
    
    /**
     * Validate wholesale form fields
     */
    function validateWholesaleForm() {
      // Validate all radio groups (except monthly retail volume which is conditional)
      let isValid = true;
      wholesaleRadioGroups.forEach(radioGroup => {
        // Skip monthly retail volume if it's hidden
        if (radioGroup === monthlyRetailVolumeRadioGroup) {
          const retailSelection = getRetailSelection();
          if (retailSelection === 'yes') {
            // Only validate if retail is "yes"
            if (!validateRadioGroup(radioGroup)) {
              isValid = false;
              radioGroup.classList.add('field--error');
            } else {
              radioGroup.classList.remove('field--error');
            }
          } else {
            // Not required if retail is "no" or not selected
            radioGroup.classList.remove('field--error');
          }
        } else {
          // Validate other radio groups normally
          if (!validateRadioGroup(radioGroup)) {
            isValid = false;
            radioGroup.classList.add('field--error');
          } else {
            radioGroup.classList.remove('field--error');
          }
        }
      });
      
      // Validate all checkbox groups
      wholesaleCheckboxGroups.forEach(checkboxGroup => {
        if (!validateCheckboxGroup(checkboxGroup)) {
          isValid = false;
          checkboxGroup.classList.add('field--error');
        } else {
          checkboxGroup.classList.remove('field--error');
        }
      });
      
      return isValid;
    }
    
    /**
     * Add custom validation for radio groups and checkboxes
     */
    function addCustomValidation() {
      if (!form) return;
      
      // Use capture phase to run before other handlers
      form.addEventListener('submit', function(e) {
        const selectedValue = getSelectedValue(subjectSelect);
        const selectedText = getSelectedText(subjectSelect);
        const isWholesale = WHOLESALE_VALUES.includes(selectedValue) || 
                           WHOLESALE_VALUES.includes(selectedText);
        
        if (!isWholesale) return;
        
        const isValid = validateWholesaleForm();
        
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Also prevent any default HTML5 validation from submitting
          if (form.checkValidity && form.checkValidity()) {
            // Form would pass HTML5 validation, but we need to block it
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
          }
          
          // Scroll to first error
          const firstError = wholesaleFields.querySelector('.field--error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          return false;
        }
      }, true); // Use capture phase
    }
    
    /**
     * Toggle wholesale fields visibility and required state
     */
    function toggleWholesaleFields() {
      const selectedValue = getSelectedValue(subjectSelect);
      const selectedText = getSelectedText(subjectSelect);
      const isWholesale = WHOLESALE_VALUES.includes(selectedValue) || 
                         WHOLESALE_VALUES.includes(selectedText);
      
      if (isWholesale) {
        // Show wholesale fields
        wholesaleFields.classList.remove('hidden');
        
        // Hide the "Your message" textarea and remove required attribute
        if (messageField && messageTextarea) {
          messageField.classList.add('hidden');
          messageTextarea.removeAttribute('required');
          messageTextarea.removeAttribute('aria-required');
        }
        
        // Update submit button text
        if (submitButton && wholesaleSubmitText) {
          submitButton.textContent = wholesaleSubmitText;
        }
        
        // Make text inputs and textareas required
        wholesaleInputs.forEach(input => {
          // Only set required for non-radio, non-checkbox inputs (text inputs and textareas)
          if (input.type !== 'radio' && input.type !== 'checkbox' && input.tagName !== 'TEXTAREA') {
            input.setAttribute('required', 'required');
            input.setAttribute('aria-required', 'true');
          } else if (input.tagName === 'TEXTAREA') {
            // Textareas are optional based on requirements, but we can make them required if needed
            // For now, they're optional as per the spec
          }
        });
        
        // Remove error classes when showing
        wholesaleRadioGroups.forEach(group => {
          group.classList.remove('field--error');
        });
        wholesaleCheckboxGroups.forEach(group => {
          group.classList.remove('field--error');
        });
        
        // Initialize monthly retail volume section state
        toggleMonthlyRetailVolume();
      } else {
        // Hide wholesale fields
        wholesaleFields.classList.add('hidden');
        
        // Show the "Your message" textarea and restore required attribute
        if (messageField && messageTextarea) {
          messageField.classList.remove('hidden');
          messageTextarea.setAttribute('required', 'required');
          messageTextarea.setAttribute('aria-required', 'true');
        }
        
        // Restore default submit button text
        if (submitButton && defaultSubmitText) {
          submitButton.textContent = defaultSubmitText;
        }
        
        // Remove required attribute and clear values
        wholesaleInputs.forEach(input => {
          input.removeAttribute('required');
          input.removeAttribute('aria-required');
          if (input.type === 'text' || input.tagName === 'TEXTAREA') {
            input.value = '';
          } else if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = false;
          }
        });
        
        // Remove error classes
        wholesaleRadioGroups.forEach(group => {
          group.classList.remove('field--error');
        });
        wholesaleCheckboxGroups.forEach(group => {
          group.classList.remove('field--error');
        });
        
        // Hide monthly retail volume section when wholesale fields are hidden
        if (monthlyRetailVolumeSection) {
          monthlyRetailVolumeSection.classList.add('hidden');
        }
      }
    }
    
    // Initialize custom validation
    addCustomValidation();
    
    // Also prevent button click if validation fails (backup method)
    if (submitButton) {
      submitButton.addEventListener('click', function(e) {
        const selectedValue = getSelectedValue(subjectSelect);
        const selectedText = getSelectedText(subjectSelect);
        const isWholesale = WHOLESALE_VALUES.includes(selectedValue) || 
                           WHOLESALE_VALUES.includes(selectedText);
        
        if (!isWholesale) return;
        
        const isValid = validateWholesaleForm();
        
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Scroll to first error
          const firstError = wholesaleFields.querySelector('.field--error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          return false;
        }
      }, true);
    }
    
    // Add real-time validation feedback for radio buttons and checkboxes
    wholesaleRadioGroups.forEach(radioGroup => {
      const radios = radioGroup.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.addEventListener('change', function() {
          if (this.checked) {
            radioGroup.classList.remove('field--error');
          }
        });
      });
    });
    
    // Add event listeners to retail radio buttons to toggle monthly retail volume section
    if (retailRadioButtons.length > 0) {
      retailRadioButtons.forEach(radio => {
        radio.addEventListener('change', function() {
          toggleMonthlyRetailVolume();
        });
      });
    }
    
    wholesaleCheckboxGroups.forEach(checkboxGroup => {
      const checkboxes = checkboxGroup.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', function() {
          if (validateCheckboxGroup(checkboxGroup)) {
            checkboxGroup.classList.remove('field--error');
          }
        });
      });
    });

    // Listen for changes on the subject select
    // Works for both native select and Choices.js (which updates the select value)
    subjectSelect.addEventListener('change', toggleWholesaleFields);
    
    // Also listen for Choices.js custom events if it's being used
    // Choices.js fires a 'choice' event on the document when selection changes
    document.addEventListener('choice', function(event) {
      // Check if this event is for our select element
      if (event.detail && event.detail.id === subjectSelect.id) {
        toggleWholesaleFields();
      }
    });
    
    // Use MutationObserver to watch for value changes (works with Choices.js)
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          toggleWholesaleFields();
        }
      });
    });
    
    // Observe the select element for attribute changes
    observer.observe(subjectSelect, {
      attributes: true,
      attributeFilter: ['value']
    });
    
    // Also check periodically in case Choices.js updates the value differently
    // This is a fallback for any edge cases
    let lastValue = subjectSelect.value;
    const valueCheckInterval = setInterval(function() {
      if (subjectSelect.value !== lastValue) {
        lastValue = subjectSelect.value;
        toggleWholesaleFields();
      }
    }, 200);
    
    // Stop checking after form is submitted or page unloads
    if (form) {
      form.addEventListener('submit', function() {
        clearInterval(valueCheckInterval);
        observer.disconnect();
      });
    }
    
    // Check initial state in case form is pre-filled
    // Delay slightly to allow Choices.js to initialize if needed
    setTimeout(function() {
      toggleWholesaleFields();
      // Initialize monthly retail volume section state
      toggleMonthlyRetailVolume();
    }, 100);
  }

  /**
   * Hide subpage element if form was successfully submitted
   */
  function hideSubpageOnSuccess() {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const contactPosted = urlParams.get('contact_posted');
    
    if (contactPosted === 'true') {
      const subpageElement = document.querySelector('.subpage');
      if (subpageElement) {
        subpageElement.classList.add('hidden');
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initWholesaleFields();
      hideSubpageOnSuccess();
    });
  } else {
    initWholesaleFields();
    hideSubpageOnSuccess();
  }
})();
